import {
  calculateDealerPrice,
  formatWithCommasWithoutDecimal,
} from "@/utils/helpers";
import {
  Box,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Image from "next/image";
import React from "react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useAppSelector } from "@/hooks/useAppSelector";
const IMAGE_BASE = process.env.NEXT_PUBLIC_IMAGE_BASE;

interface ViewMyDiscountProps {
  open: boolean;
  handleClose: () => void;
}

const ViewMyDiscount: React.FC<ViewMyDiscountProps> = ({
  open,
  handleClose,
}) => {
  // Process discount data dynamically
  const { cart } = useAppSelector((state) => state.cart);
  const { manufacturers } = useAppSelector((state) => state.home);
  // 🔁 Flatten the 3 categories
  const allManufacturers = [
    ...(manufacturers?.domestic || []),
    ...(manufacturers?.premium || []),
    ...(manufacturers?.others || []),
  ];
  const discountData = allManufacturers
    ?.filter(
      (brand) => brand?.MOQ_slab !== null && brand?.MOQ_slab !== undefined
    ) // Filter out brands with null or 0 MOQ_slab
    ?.map((brand) => {
      const cartBrand = cart?.manufacturers?.find(
        (m) => m?.manufacture_name === brand?.manufacture_name
      );
      return {
        brand: brand?.manufacture_name,
        brandLogo: `https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${IMAGE_BASE}_MANUFACTURE_IMAGE/${brand?.manufacture_images}`,
        discountSlab: brand?.additional_discount_slab
          ? `${
              brand?.discount
            }% discount for orders above ₹${formatWithCommasWithoutDecimal(
              brand?.additional_discount_slab
            )}`
          : "No discount available",
        addedAmount: cartBrand
          ? cartBrand.products?.reduce((acc, p) => {
              const ndpPrice = calculateDealerPrice(
                Number(p?.MRP_price),
                Number(
                  p?.buy_quantity &&
                    p?.Type === "sales" &&
                    p?.Sales_Qunatity !== null &&
                    p?.Sales_Qunatity !== 0 &&
                    p?.Sales_Price !== null &&
                    Number(p?.Sales_Price) !== 0
                    ? p?.Sales_Price
                    : p?.NDP_Price
                ),
                p?.GST
              );

              // Use Offer_price if it's greater than 0, otherwise, use NDP_Price
              const priceToUse = ndpPrice;

              return acc + priceToUse * (p?.buy_quantity || 0);
            }, 0)
          : 0,

        minAmount: brand?.MOQ_slab || 0,
        discountAdded: cartBrand
          ? cartBrand.products?.reduce((acc, p) => {
              const ndpPrice = calculateDealerPrice(
                Number(p.MRP_price),
                Number(
                  p?.buy_quantity &&
                    p?.Sales_Qunatity !== null &&
                    p?.Sales_Qunatity !== 0 &&
                    p?.Sales_Price !== null &&
                    Number(p?.Sales_Price) !== 0 &&
                    p?.buy_quantity >= p?.Sales_Qunatity
                    ? p?.Sales_Price
                    : p?.NDP_Price
                ),
                p.GST
              );

              // Use Offer_price if it's greater than 0, otherwise, use NDP_Price
              const priceToUse = ndpPrice;

              return acc + priceToUse * (p?.buy_quantity || 0);
            }, 0)
          : 0,
        discountUse: cartBrand
          ? cartBrand.products?.reduce((acc, p) => {
              const ndpPrice = Number(
                p?.buy_quantity &&
                  p?.Sales_Qunatity !== null &&
                  p?.Sales_Qunatity !== 0 &&
                  p?.Sales_Price !== null &&
                  Number(p?.Sales_Price) !== 0 &&
                  p?.buy_quantity >= p?.Sales_Qunatity
                  ? p?.Sales_Price
                  : p?.NDP_Price
              );

              // Use Offer_price if it's greater than 0, otherwise, use NDP_Price
              const priceToUse = ndpPrice;

              return acc + priceToUse * (p?.buy_quantity || 0);
            }, 0)
          : 0,
        discountMin: brand?.additional_discount_slab
          ? brand?.additional_discount_slab
          : 0,
      };
    });
  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "75%",
          maxWidth: "660px",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: "10px",
        }}
      >
        <CloseOutlinedIcon
          style={{ fontSize: "16px" }}
          className="text-white-1 absolute top-1 flex z-10 right-1 cursor-pointer"
          onClick={handleClose}
        />

        <TableContainer
          sx={{
            maxHeight: 600,
            overflowY: "auto",
            // scrollbarGutter: "stable",
            "&::-webkit-scrollbar": {
              display: "block !important", // Overrides global CSS
              width: "8px !important",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#283248",
                }}
              >
                <TableCell
                  align="center"
                  sx={{
                    width: "20%",
                    color: "white",
                    fontWeight: "bold",
                    borderTopLeftRadius: "10px",
                    letterSpacing: "1px",
                    padding: "10px",
                    fontFamily: "inherit",
                    backgroundColor: "#283248",
                  }}
                >
                  Brands
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    width: "40%",
                    color: "white",
                    fontWeight: "bold",
                    padding: "10px",
                    fontFamily: "inherit",
                    letterSpacing: "1px",
                    backgroundColor: "#283248",
                  }}
                >
                  MOV Slab
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    width: "40%",
                    color: "white",
                    fontWeight: "bold",
                    borderTopRightRadius: "10px",
                    padding: "10px",
                    fontFamily: "inherit",
                    letterSpacing: "1px",
                    backgroundColor: "#283248",
                  }}
                >
                  Additional Discount Slab
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
          <div style={{ maxHeight: "500px", overflowY: "auto" }}>
            <Table>
              <TableBody>
                {discountData?.map((row, index) => {
                  const hasItemsInCart = row?.addedAmount > 0;
                  return (
                    <TableRow key={index}>
                      {/* Brands */}
                      <TableCell
                        align="center"
                        sx={{
                          padding: "10px",
                          width: "20%",
                          borderBottom:
                            index === discountData?.length - 1 ? "none" : "",
                        }}
                      >
                        <Image
                          quality={50}
                          property="no"
                          unoptimized
                          src={row?.brandLogo}
                          alt={row?.brand}
                          width={60}
                          height={30}
                          style={{
                            objectFit: "contain",
                            margin: "0 auto",
                          }}
                        />
                      </TableCell>

                      {/* MOQ Slab */}
                      <TableCell
                        align="left"
                        sx={{
                          padding: "10px",
                          width: "40%",
                          fontFamily: "inherit",
                          borderBottom:
                            index === discountData?.length - 1 ? "none" : "",
                        }}
                      >
                        <div
                          className={`${
                            hasItemsInCart ? "bg-green-100" : "bg-[#f1f1f1]"
                          } rounded-md p-2`}
                        >
                          <span className="font-bold text-sm">
                            ₹{formatWithCommasWithoutDecimal(row?.addedAmount)}{" "}
                            / ₹{formatWithCommasWithoutDecimal(row?.minAmount)}
                          </span>
                          <div
                            style={{
                              height: "3px",
                              backgroundColor: "white",
                              borderRadius: "5px",
                              marginTop: "5px",
                              position: "relative",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${Math.min(
                                  (row?.addedAmount / row?.minAmount) * 100,
                                  100
                                )}%`,
                                height: "100%",
                                backgroundColor: "green",
                              }}
                            ></div>
                          </div>
                          <span
                            className={`text-[10px] ${
                              row?.addedAmount >= row?.minAmount
                                ? "text-blue font-[700]"
                                : "text-gray-600"
                            }`}
                          >
                            {row?.addedAmount >= row?.minAmount
                              ? "Congratulation! You met MOV"
                              : "Add more items to place order"}
                          </span>
                        </div>
                      </TableCell>

                      {/* Additional Discount Slab */}
                      <TableCell
                        align="left"
                        sx={{
                          padding: "10px",
                          width: "40%",
                          fontFamily: "inherit",
                          borderBottom:
                            index === discountData?.length - 1 ? "none" : "",
                        }}
                      >
                        <div
                          className={`${
                            hasItemsInCart ? "bg-green-100" : "bg-[#f1f1f1]"
                          } rounded-md p-2`}
                        >
                          <span className="font-bold text-sm">
                            ₹
                            {formatWithCommasWithoutDecimal(row?.discountAdded)}{" "}
                            / ₹
                            {formatWithCommasWithoutDecimal(row?.discountMin)}
                          </span>
                          <div
                            style={{
                              height: "3px",
                              backgroundColor: "white",
                              borderRadius: "5px",
                              marginTop: "5px",
                              position: "relative",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${Math.min(
                                  (row?.discountAdded / row?.discountMin) * 100,
                                  100
                                )}%`,
                                height: "100%",
                                backgroundColor: "green",
                              }}
                            ></div>
                          </div>
                          <span
                            className={`text-[10px] ${
                              row?.discountMin !== 0 &&
                              row?.discountAdded >= row?.discountMin
                                ? "text-blue font-[700]"
                                : "text-gray-600"
                            }`}
                          >
                            {row?.discountMin !== 0 &&
                            row?.discountAdded >= row?.discountMin
                              ? `Congratulation! You got ₹${formatWithCommasWithoutDecimal(
                                  (row?.discountUse * 2) / 100
                                )} discount`
                              : row?.discountSlab}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TableContainer>
      </Box>
    </Modal>
  );
};

export default ViewMyDiscount;
